import { CSSProperties, useContext, useLayoutEffect, useState } from 'react';

import { LeftOutlined, RightOutlined, MenuOutlined } from '@ant-design/icons';
import { Button, Menu, MenuProps, Tooltip, Drawer } from 'antd';
import Sider from 'antd/lib/layout/Sider';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import classNames from 'classnames/bind';
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom';

import { AstriHubLogo } from '@/components/general/AstriHubLogo';
import { ThemeButton } from '@/components/general/ThemeButton';

import { SidenavSlotContext } from './slot';
import styles from './style.module.less';

const cx = classNames.bind(styles);

export type SidenavProps = {
  items: ItemType[];
  onSelectItem: MenuProps['onClick'];
  defaultOpenKeys?: string[];
  defaultCollapsed?: boolean;
  width?: number;
  // Where the logo of the menu leads to.
  logoLink?: string;
};

export const Sidenav = ({
  items,
  onSelectItem,
  defaultOpenKeys,
  defaultCollapsed = false,
  width = 200,
  logoLink,
}: SidenavProps) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Starting collapsed, no group may start open: a collapsed menu shows its open groups as a popup.
  const [openKeys, setOpenKeys] = useState<string[]>(
    defaultCollapsed ? [] : defaultOpenKeys ?? [],
  );
  const [drawerVisible, setDrawerVisible] = useState(false);
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const slot = useContext(SidenavSlotContext);
  const slotElement = isMobile ? null : slot?.element;
  const setDocked = slot?.setDocked;

  // Docked, the menu carries the logo itself, so the layout drops the one in its header.
  useLayoutEffect(() => {
    if (!slotElement || !setDocked) return undefined;

    setDocked(true);

    return () => setDocked(false);
  }, [slotElement, setDocked]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  if (isMobile) {
    return (
      <>
        <Button
          icon={<MenuOutlined />}
          onClick={() => setDrawerVisible(true)}
          style={{ position: 'fixed', top: 16, left: 16, zIndex: 99999 }}
        />
        <Drawer
          title="Menu"
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
        >
          <div className="theme-button-side">
            <ThemeButton />
          </div>

          <Menu
            defaultSelectedKeys={[location.pathname]}
            defaultOpenKeys={defaultOpenKeys}
            mode="inline"
            items={items}
            onClick={(e) => {
              if (onSelectItem) {
                onSelectItem(e);
              }
              setDrawerVisible(false);
            }}
          />
        </Drawer>
      </>
    );
  }

  // With a layout slot the menu docks there once the slot is ready; without one it renders where
  // it is declared.
  if (slot && !slotElement) return null;

  const sider = (
    <Sider
      collapsed={collapsed}
      breakpoint="lg"
      width={width}
      collapsedWidth="48"
      theme="light"
      className={cx(`sidenav`, `sider__tools`, { 'sidenav--docked': Boolean(slotElement) })}
      style={{ '--sidenav-width': `${width}px` } as CSSProperties}
    >
      {slotElement && (
        <div className={cx(`logo-prisma`, `sidenav__brand`)}>
          <AstriHubLogo variant={collapsed ? `icon` : `full`} to={logoLink} />
        </div>
      )}

      <Tooltip
        title={collapsed ? `Expandir menu` : `Ocultar menu`}
        placement="bottom"
        className="collapse__button"
      >
        <Button
          onClick={toggleCollapsed}
          shape="circle"
          className={collapsed ? styles.sidenav__button_close : styles.sidenav__button_open}
          size="small"
          icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
          data-testid="toggleCollapsed"
        />
      </Tooltip>

      <Menu
        defaultSelectedKeys={[location.pathname]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        mode="inline"
        items={items}
        className={styles.sidenav__opened}
        onClick={onSelectItem}
      />
    </Sider>
  );

  return slotElement ? createPortal(sider, slotElement) : sider;
};
